import React from 'react';

import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { DataTableFilterMetaData } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { TriStateCheckbox, TriStateCheckboxChangeEvent } from 'primereact/tristatecheckbox';
import { Nullable } from 'primereact/ts-helpers';
import { classNames } from 'primereact/utils';

import { Asset } from '../../types/interfaces';
import { clearFilter } from '../../utils';

interface AssetColumnsProps {
  filters: Record<string, DataTableFilterMetaData>;
  setFilters: React.Dispatch<
    React.SetStateAction<Record<string, DataTableFilterMetaData>>
  >;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  setFirst: React.Dispatch<React.SetStateAction<number>>;
  handleInputChange: (
    filter: string
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  assetNameFilterRef: React.RefObject<HTMLInputElement>;
  fullAddressFilterRef: React.RefObject<HTMLInputElement>;
  geographicalZoneFilterRef: React.RefObject<HTMLInputElement>;
  descriptionFilterRef: React.RefObject<HTMLInputElement>;
  externalIdFilterRef: React.RefObject<HTMLInputElement>;
  assetTypeFilterRef: React.RefObject<HTMLInputElement>;
  columnOrder?: string[];
  columnWidths?: Record<string, string>;
  activeFilterActive: Nullable<boolean>;
  setActiveFilterActive: React.Dispatch<React.SetStateAction<Nullable<boolean>>>;
}

export const getAssetColumns = ({
  filters,
  setFilters,
  setPageNumber,
  setFirst,
  handleInputChange,
  assetNameFilterRef,
  fullAddressFilterRef,
  geographicalZoneFilterRef,
  descriptionFilterRef,
  externalIdFilterRef,
  assetTypeFilterRef,
  columnOrder = [],
  columnWidths = {},
  activeFilterActive,
  setActiveFilterActive
}: AssetColumnsProps) => {
  const stateBodyTemplate = (rowData: Asset) => {
    return (
      <i
        className={classNames('pi', {
          'true-icon pi-check-circle': rowData.state,
          'false-icon pi-times-circle': !rowData.state
        })}
      ></i>
    );
  };

  const stateRowFilterTemplate = (
    options: ColumnFilterElementTemplateOptions
  ) => {
    return (
      <TriStateCheckbox
        id="activeFilterToggle"
        value={activeFilterActive}
        onChange={(e: TriStateCheckboxChangeEvent) => {
          setActiveFilterActive(e.value);
        }}
      />
    );
  };

  const columns = [
    {
      key: 'name',
      element: (
        <Column
          columnKey="name"
          sortable
          field="name"
          header="Название объекта"
          filter
          onFilterClear={() =>
            clearFilter(
              'name',
              filters,
              setFilters,
              assetNameFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={assetNameFilterRef}
                onChange={handleInputChange('name')}
                placeholder="Поиск по названию объекта"
              />
            </div>
          }
          style={{
            width: columnWidths.name || '17%',
            wordBreak: 'break-all'
          }}
        />
      )
    },
    {
      key: 'externalId',
      element: (
        <Column
          columnKey="externalId"
          sortable
          field="externalId"
          header="Внешний номер"
          filter
          onFilterClear={() =>
            clearFilter(
              'externalId',
              filters,
              setFilters,
              externalIdFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={externalIdFilterRef}
                onChange={handleInputChange('externalId')}
                placeholder="Поиск по внешнему номеру объекта"
              />
            </div>
          }
          style={{
            minWidth: '12rem',
            width: columnWidths.externalId || '11%'
          }}
        />
      )
    },
    {
      key: 'type',
      element: (
        <Column
          columnKey="type"
          sortable
          field="type"
          header="Тип объекта"
          filter
          onFilterClear={() =>
            clearFilter(
              'type',
              filters,
              setFilters,
              assetTypeFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={assetTypeFilterRef}
                onChange={handleInputChange('type')}
                placeholder="Поиск по типу объекта"
              />
            </div>
          }
          style={{
            minWidth: '12rem',
            width: columnWidths.type || '12%'
          }}
        />
      )
    },
    {
      key: 'fullAddress',
      element: (
        <Column
          columnKey="fullAddress"
          sortable
          field="fullAddress"
          header="Адрес"
          filter
          onFilterClear={() =>
            clearFilter(
              'fullAddress',
              filters,
              setFilters,
              fullAddressFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={fullAddressFilterRef}
                onChange={handleInputChange('fullAddress')}
                placeholder="Поиск по адресу"
              />
            </div>
          }
          style={{
            width: columnWidths.fullAddress || '21%',
            minWidth: '21%'
          }}
          showFilterMenu
          showFilterMenuOptions
          showFilterOperator
          filterApply
        />
      )
    },
    {
      key: 'geographicalZone',
      element: (
        <Column
          columnKey="geographicalZone"
          sortable
          field="geographicalZone"
          header="Географическая зона"
          filter
          onFilterClear={() =>
            clearFilter(
              'geographicalZone',
              filters,
              setFilters,
              geographicalZoneFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={geographicalZoneFilterRef}
                onChange={handleInputChange('geographicalZone')}
                placeholder="Поиск по географической зоне"
              />
            </div>
          }
          style={{
            width: columnWidths.geographicalZone || '14%',
            minWidth: '14%'
          }}
          showFilterMenu
          showFilterMenuOptions
          showFilterOperator
          filterApply
        />
      )
    },
    {
      key: 'state',
      element: (
        <Column
          columnKey="state"
          sortable
          field="state"
          header="Активен"
          dataType="boolean"
          style={{ width: columnWidths.state || '5%' }}
          body={stateBodyTemplate}
          filter
          filterElement={stateRowFilterTemplate}
        />
      )
    },
    {
      key: 'description',
      element: (
        <Column
          columnKey="description"
          sortable
          field="description"
          header="Описание"
          filter
          onFilterClear={() =>
            clearFilter(
              'description',
              filters,
              setFilters,
              descriptionFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={descriptionFilterRef}
                onChange={handleInputChange('description')}
                placeholder="Поиск по описанию"
              />
            </div>
          }
          style={{ width: columnWidths.description || '20%' }}
        />
      )
    }
  ];

  // Сортируем по сохраненному порядку
  if (columnOrder.length > 0) {
    const orderMap = new Map(columnOrder.map((key, index) => [key, index]));
    return columns.sort((a, b) => {
      const aIndex = orderMap.get(a.key) ?? Infinity;
      const bIndex = orderMap.get(b.key) ?? Infinity;
      return aIndex - bIndex;
    });
  }

  return columns;
};
